/**
 * NOTE: This file is part of the legacy Notes feature.
 * User-facing text has been renamed to "Documentation" for clarity.
 * For the new Notes feature, check the Hub implementation.
 */

import {
  Note,
  useDeleteNote,
  useEditNote,
  useShareNote,
  useUnshareNote,
} from "@/lib/api/notes.ts";
import {
  HeadingType,
  NoteTimelineItem,
} from "@/components/conversation-details/notes/note-timeline-item.tsx";
import { useCallback, useState } from "react";
import { useNoteUtils } from "@/lib/hooks/conversations/use-note-utils.ts";
import { NoteSchema } from "@/lib/hooks/conversations/use-note-form.tsx";
import { formatDate } from "@/lib/utils.ts";
import { useAuth } from "@/components/auth/auth-context";
import { PostHogEvents, useTrackEvent } from "@/lib/posthog-events";

interface NoteRendererProps {
  note: Note;

  pinnedNotes?: boolean;
  readonlyMode?: boolean;
  adminMode?: boolean;

  menteeId: string;
  mentorId: string;
}

export function NoteRenderer({
  note,
  pinnedNotes,
  readonlyMode,
  adminMode,
  menteeId,
  mentorId,
}: NoteRendererProps) {
  const { user } = useAuth();
  const { mutateAsync: updateNote } = useEditNote();
  const { mutateAsync: deleteNote } = useDeleteNote();
  const { mutateAsync: shareNote } = useShareNote();
  const { mutateAsync: unshareNote } = useUnshareNote();
  const [currentNote, setCurrentNote] = useState<Note | null>(null);
  const { isNotePinned, convertFormValuesToReminder } = useNoteUtils();
  const { track } = useTrackEvent();

  const updateNoteHandler = async (payload: NoteSchema) => {
    if (!currentNote || readonlyMode) {
      return;
    }

    const reminder = convertFormValuesToReminder(
      payload.date,
      payload.time,
      payload.timezone,
    );

    // We don't have to wait for the update to finish
    updateNote({
      id: currentNote.id,
      title: payload.title,
      content: payload.content,
      mentorshipId: currentNote.mentorshipId,
      reminder: reminder || null,
      metadata: {
        ...(currentNote.metadata || {}),
        reminderTimezone: payload.timezone,
      },
    });

    track(PostHogEvents.DOCUMENT_EDITED, {
      mentorship_id: currentNote.mentorshipId,
      note_id: currentNote.id,
      has_reminder: !!reminder,
    });

    setCurrentNote(null);
  };

  const togglePin = useCallback(
    async (note: Note) => {
      const isPinned = isNotePinned(note);

      if (isPinned) {
        await updateNote({
          id: note.id,
          mentorshipId: note.mentorshipId,
          metadata: {
            ...(note.metadata || {}),
            pinnedByMentor: null,
          },
        });
      } else {
        await updateNote({
          id: note.id,
          mentorshipId: note.mentorshipId,
          metadata: {
            ...(note.metadata || {}),
            pinnedByMentor: {
              value: true,
              updatedAt: new Date().toISOString(),
            },
          },
        });
      }
    },
    [isNotePinned],
  );

  const getHeading = useCallback(
    (note: Note) => {
      const isNoteCreator = note.createdBy.id === user?.id;

      if (note.reminder) {
        return {
          text: `${isNoteCreator ? "You" : note.createdBy?.name} set a reminder for ${formatDate(note.reminder)}`,
          type: "reminder",
        };
      }

      if (
        pinnedNotes &&
        note.metadata?.pinnedByMentor &&
        typeof note.metadata?.pinnedByMentor === "object"
      ) {
        const pinnedAt =
          "updatedAt" in note.metadata.pinnedByMentor
            ? (note.metadata?.pinnedByMentor?.updatedAt as string)
            : note.createdAt;

        return {
          text: `${isNoteCreator ? "You" : note.createdBy?.name} pinned documentation on ${formatDate(pinnedAt)}`,
          type: "pinned",
        };
      }

      return {
        text: `${isNoteCreator ? "You" : note.createdBy?.name} created documentation on ${formatDate(note.createdAt)}`,
        type: "created",
      };
    },
    [pinnedNotes, user?.id],
  );

  const { text, type } = getHeading(note);

  return (
    <NoteTimelineItem
      key={note.id}
      heading={text}
      headingType={type as HeadingType}
      note={note}
      noteActions={{
        handleSave: updateNoteHandler,
        onDelete: () => {
          if (readonlyMode) return;

          deleteNote({
            id: note.id,
            mentorshipId: note.mentorshipId,
          });
        },
        togglePin: () => {
          if (readonlyMode) return;

          togglePin(note);
        },
        shareWith: (shareWith: "MENTEE" | "MENTOR") => {
          if (readonlyMode) return;

          const targetId = shareWith === "MENTEE" ? menteeId : mentorId;
          const isShared = note.sharedWith?.includes(targetId);

          if (isShared) {
            unshareNote({
              id: note.id,
              shareWithId: targetId,
              mentorshipId: note.mentorshipId,
            });
          } else {
            shareNote({
              id: note.id,
              shareWithId: targetId,
              mentorshipId: note.mentorshipId,
            });
          }
        },
      }}
      cardActions={{
        onClick: () => {
          if (readonlyMode) return;

          setCurrentNote(note);
        },
        onCancel: () => {
          setCurrentNote(null);
        },
      }}
      editMode={currentNote?.id === note.id}
      readonlyMode={readonlyMode}
      adminMode={adminMode}
      isSharedWithMentee={note.sharedWith?.includes(menteeId)}
      isSharedWithMentor={note.sharedWith?.includes(mentorId)}
    />
  );
}
